import React, { useState } from "react";
import { ShieldCheck, ChevronDown, Quote, ArrowRight, Sparkles, Phone, Mail } from "lucide-react";
import { FAQS, TESTIMONIALS, COURSE_META, CONTACT, ELEARNING_COURSE_URL } from "@/data/certificationProgramData";
import { CERTIFICATION_WHATSAPP_LINK } from "@/components/CertificationEnquiryModal";

export function CertificationSection({ onEnquiry }) {
  return (
    <div className="mb-10 glass-panel rounded-2xl p-8 border border-amber-400/20" data-testid="certification-certificate-section">
      <div className="flex flex-col sm:flex-row items-start gap-6">
        <ShieldCheck className="w-8 h-8 text-amber-300 shrink-0" />
        <div className="flex-1">
          <h2 className="font-heading text-xl font-semibold text-white mb-2">A certificate that signals real capability.</h2>
          <p className="text-sm text-slate-400 mb-3">
            On completion, you receive a verified Course Completion Certificate from E-Learning IT covering all 12 modules and
            5 real-time projects — plus structured exam-prep support for the official Salesforce Data Cloud Certification.
          </p>
          <ul className="space-y-1.5 mb-4">
            <li className="text-sm text-slate-300 flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Verifiable digital certificate with unique ID</li>
            <li className="text-sm text-slate-300 flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Detailed project & skills transcript for recruiters</li>
            <li className="text-sm text-slate-300 flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Guided prep track for Salesforce's official Data Cloud exam</li>
          </ul>
          <button onClick={onEnquiry} data-testid="certification-get-certified-button" className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-colors">
            Get Certified <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function FaqSection() {
  const [open, setOpen] = useState(-1);
  return (
    <div className="mb-10" data-testid="certification-faq-section">
      <h2 className="font-heading text-2xl font-semibold text-white mb-1">Questions, answered.</h2>
      <p className="text-sm text-slate-400 mb-5">FAQs</p>
      <div className="space-y-2" data-testid="certification-faq-list">
        {FAQS.map((f, i) => (
          <div key={i} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`certification-faq-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`certification-faq-toggle-${i}`} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <span className="text-sm font-medium text-white">{f.q}</span>
              <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && (
              <div className="px-4 py-3.5 border-t border-white/10" data-testid={`certification-faq-answer-${i}`}>
                <p className="text-sm text-slate-400 leading-relaxed">{f.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  return (
    <div className="mb-10" data-testid="certification-testimonials-section">
      <h2 className="font-heading text-2xl font-semibold text-white mb-1">Careers changed, not just skills.</h2>
      <p className="text-sm text-slate-400 mb-5">Testimonials</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="certification-testimonials-grid">
        {TESTIMONIALS.map((t, i) => (
          <div key={i} className="glass-panel rounded-xl p-5" data-testid={`certification-testimonial-${i}`}>
            <Quote className="w-4 h-4 text-amber-400/50 mb-2" />
            <p className="text-xs text-slate-300 leading-relaxed mb-4">"{t.quote}"</p>
            <div className="flex items-center gap-2.5 pt-3 border-t border-white/5">
              <span className="w-8 h-8 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 text-[11px] font-bold flex items-center justify-center shrink-0">{t.initials}</span>
              <div>
                <p className="text-xs font-semibold text-white">{t.name}</p>
                <p className="text-[10px] text-slate-500">{t.roleChange}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FinalCtaSection({ onEnquiry }) {
  return (
    <div className="glass-panel rounded-2xl p-8 text-center border border-amber-400/20" data-testid="certification-bottom-cta">
      <h3 className="font-heading text-xl font-semibold text-white">Your Data Cloud career starts with one decision.</h3>
      <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
        Seats for the next live cohort are limited to maintain 1:1 mentorship quality. Enroll now or talk to us to experience
        a live session first.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
        <a href={ELEARNING_COURSE_URL} target="_blank" rel="noreferrer" data-testid="certification-bottom-enroll-button" className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm px-6 py-3 rounded-xl transition-colors">
          <ArrowRight className="w-4 h-4" /> Enroll Now — ₹{COURSE_META.price.toLocaleString("en-IN")}
        </a>
        <button onClick={onEnquiry} data-testid="certification-bottom-enquire-button" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-sm px-6 py-3 rounded-xl transition-colors">
          <Sparkles className="w-4 h-4 text-amber-300" /> Learn Like an Internship
        </button>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
        <a href={CERTIFICATION_WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="certification-whatsapp-link" className="flex items-center gap-1.5 text-xs font-semibold text-[#25D366] hover:text-[#20BD5A] px-3 py-2">
          WhatsApp Us
        </a>
        <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="certification-call-link" className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white px-3 py-2">
          <Phone className="w-3.5 h-3.5" /> {CONTACT.phone}
        </a>
        <a href={`mailto:${CONTACT.email}`} data-testid="certification-email-link" className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white px-3 py-2">
          <Mail className="w-3.5 h-3.5" /> {CONTACT.email}
        </a>
      </div>
    </div>
  );
}
